import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import type { StampRotationMode } from "../../utils/artbrushStampBrush";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import {
    fitReferenceImageToCanvas,
    referenceFullCanvasPlacement,
    resizeReferenceKeepingCenter,
    type ArtbrushReferenceImage,
} from "../../utils/artbrushReference";
import {
    buildMultiBrushStrokesFromSamples,
    buildArtbrushExportSvgString,
    type BrushStroke,
    type BrushPointerSample,
} from "../../utils/artbrushStroke";
import {
    applyStampStrokeUnion,
    cloneStampStrokes,
    createActiveStampStroke,
    extendStampStrokeWithPoints,
    finalizeStampStroke,
    parseStampObjectSvg,
    type ActiveStampStroke,
    type ParsedStampObject,
    type StampRenderOptions,
    type StampStroke,
    type StampTrailPoint,
} from "../../utils/artbrushStampBrush";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import {
    downscaleRasterDataUrlIfNeeded,
    LATTICE_RASTER_MAX_EDGE,
    loadImageNaturalSize,
    readFileAsDataUrl,
} from "../../utils/latticeRasterSource";

export type ArtbrushToolKind = "brush" | "stamp" | "eraser";

export interface ArtbrushDocument {
    brushStrokes: BrushStroke[];
    stampStrokes: StampStroke[];
}

interface UploadedObjectSvg {
    name: string;
    content: string;
}

export type PenPointerPhase =
    | "idle"
    | "down"
    | "move"
    | "up"
    | "cancel"
    | "leave"
    | "enter";

/** Latest pointer sample for pen/tablet debugging. */
export interface PenPointerSnapshot {
    phase: PenPointerPhase;
    pressed: boolean;
    pointerId: number;
    pointerType: string;
    button: number;
    buttons: number;
    isPrimary: boolean;
    clientX: number;
    clientY: number;
    svgX: number | null;
    svgY: number | null;
    pressure: number;
    tiltX: number;
    tiltY: number;
    twist: number;
    tangentialPressure: number;
    width: number;
    height: number;
    coalescedCount: number;
    timestamp: number;
}

export interface PointerSample {
    x: number;
    y: number;
    pressure: number;
    pointerType: string;
}

const DEFAULT_CANVAS_W = 1000;
const DEFAULT_CANVAS_H = 1000;
const MIN_CANVAS_DIM = 100;
const MAX_CANVAS_DIM = 8192;

function clampCanvasDim(n: number): number {
    return Math.max(
        MIN_CANVAS_DIM,
        Math.min(MAX_CANVAS_DIM, Math.round(n)),
    );
}

function canvasDimsForReferenceImage(imgW: number, imgH: number): {
    canvasW: number;
    canvasH: number;
} {
    return {
        canvasW: clampCanvasDim(imgW),
        canvasH: clampCanvasDim(imgH),
    };
}
const DEFAULT_BRUSH_SIZE = 24;
const DEFAULT_MIN_PRESSURE_FACTOR = 0.12;
const DEFAULT_BRUSH_STROKE_COUNT = 1;
const DEFAULT_BRUSH_MAX_SPREAD_PX = 200;
const DEFAULT_STROKE_SMOOTHING = 0;
const DEFAULT_ERASER_SIZE = 36;
const DEFAULT_COLOR = "#1a1a1a";
const DEFAULT_REFERENCE_OPACITY = 0.35;
const DEFAULT_RESIZE_CANVAS_TO_REFERENCE = false;
const DEFAULT_STAMP_DISTANCE = 24;
const DEFAULT_STAMP_OBJECT_SIZE = 32;
const DEFAULT_STAMP_PRESSURE_SIZE_ENABLED = false;
const DEFAULT_STAMP_PRESSURE_MIN_FACTOR = 0.35;
const DEFAULT_STAMP_OBJECT_PLACEMENT_MODE: "alternating" | "random" =
    "alternating";
const DEFAULT_STAMP_COLOR_ENABLED = false;
const DEFAULT_STAMP_COLOR_MODE:
    | "single"
    | "random-per-stroke"
    | "random-per-instance"
    | "random-palette-per-instance" = "single";
const DEFAULT_STAMP_COLOR_SINGLE = "#1a1a1a";
const DEFAULT_STAMP_COLOR_PALETTE = ["#ff6b6b", "#ffd166", "#06d6a0", "#118ab2"];
const DEFAULT_STAMP_PERPENDICULAR_SCATTER_PX = 0;
const DEFAULT_STAMP_ANCHOR = "center";
const DEFAULT_STAMP_MERGE_UNION_ON_STROKE_END = false;
const DEFAULT_STAMP_ROTATION_MODE: StampRotationMode = "align";
const DEFAULT_STAMP_POLAR_CENTER_PCT = 50;
const MAX_RECENT_COLORS = 12;
const ARTBRUSH_CANVAS_DIMENSIONS_STORAGE_KEY =
    "artbrush-svg-canvas:dimensions:v1";

function defaultStampObjectSvg(): UploadedObjectSvg | null {
    const square = OBJECT_SVG_PRESETS.find((p) => p.id === "square");
    if (!square || !isValidSvg(square.svg)) return null;
    return { name: `${square.label} (preset)`, content: square.svg };
}

function defaultStampObjectSvgs(): UploadedObjectSvg[] {
    const object = defaultStampObjectSvg();
    return object ? [object] : [];
}

function cloneDocument(doc: ArtbrushDocument): ArtbrushDocument {
    return {
        brushStrokes: cloneStrokes(doc.brushStrokes),
        stampStrokes: cloneStampStrokes(doc.stampStrokes),
    };
}

function samplesToTrailPoints(
    samples: PointerSample[],
): StampTrailPoint[] {
    return samples.map((s) => ({
        x: s.x,
        y: s.y,
        pressure: s.pressure,
        pointerType: s.pointerType,
    }));
}

const IDLE_SNAPSHOT: PenPointerSnapshot = {
    phase: "idle",
    pressed: false,
    pointerId: -1,
    pointerType: "none",
    button: -1,
    buttons: 0,
    isPrimary: false,
    clientX: 0,
    clientY: 0,
    svgX: null,
    svgY: null,
    pressure: 0,
    tiltX: 0,
    tiltY: 0,
    twist: 0,
    tangentialPressure: 0,
    width: 0,
    height: 0,
    coalescedCount: 0,
    timestamp: 0,
};

function cloneStrokes(strokes: BrushStroke[]): BrushStroke[] {
    return strokes.map((s) => ({
        ...s,
        points: s.points.map((p) => ({ ...p })),
    }));
}

function strokeIntersectsEraser(
    stroke: BrushStroke,
    sample: PointerSample,
    radius: number,
): boolean {
    return stroke.points.some(
        (p) => Math.hypot(p.x - sample.x, p.y - sample.y) <= radius + p.halfWidth,
    );
}

interface ArtbrushSvgCanvasState {
    tool: ArtbrushToolKind;
    setTool: (t: ArtbrushToolKind) => void;
    canvasWidth: number;
    canvasHeight: number;
    setCanvasWidth: (n: number) => void;
    setCanvasHeight: (n: number) => void;
    viewBoxAttr: string;
    brushSize: number;
    setBrushSize: (n: number) => void;
    minPressureFactor: number;
    setMinPressureFactor: (n: number) => void;
    brushStrokeCount: number;
    setBrushStrokeCount: (n: number) => void;
    brushMaxSpreadPx: number;
    setBrushMaxSpreadPx: (n: number) => void;
    strokeSmoothing: number;
    setStrokeSmoothing: (n: number) => void;
    eraserSize: number;
    setEraserSize: (n: number) => void;
    color: string;
    setColor: (c: string) => void;
    recentColors: string[];
    strokes: BrushStroke[];
    activeStroke: BrushStroke[] | null;
    stampStrokes: StampStroke[];
    activeStampStroke: ActiveStampStroke | null;
    stampDistance: number;
    setStampDistance: (n: number) => void;
    stampObjectSize: number;
    setStampObjectSize: (n: number) => void;
    stampPressureSizeEnabled: boolean;
    setStampPressureSizeEnabled: (v: boolean) => void;
    stampPressureMinFactor: number;
    setStampPressureMinFactor: (n: number) => void;
    stampObjectPlacementMode: "alternating" | "random";
    setStampObjectPlacementMode: (m: "alternating" | "random") => void;
    stampPerpendicularScatterPx: number;
    setStampPerpendicularScatterPx: (n: number) => void;
    stampColorEnabled: boolean;
    setStampColorEnabled: (v: boolean) => void;
    stampColorMode:
        | "single"
        | "random-per-stroke"
        | "random-per-instance"
        | "random-palette-per-instance";
    setStampColorMode: (
        m:
            | "single"
            | "random-per-stroke"
            | "random-per-instance"
            | "random-palette-per-instance",
    ) => void;
    stampColorSingle: string;
    setStampColorSingle: (c: string) => void;
    stampColorPalette: string[];
    setStampColorPalette: (colors: string[]) => void;
    stampBaseRotationDeg: number;
    setStampBaseRotationDeg: (n: number) => void;
    stampRotationMode: StampRotationMode;
    setStampRotationMode: (m: StampRotationMode) => void;
    stampPolarRotationCenterXPct: number;
    setStampPolarRotationCenterXPct: (n: number) => void;
    stampPolarRotationCenterYPct: number;
    setStampPolarRotationCenterYPct: (n: number) => void;
    stampRenderOptions: StampRenderOptions;
    stampAnchor: string;
    stampMergeUnionOnStrokeEnd: boolean;
    setStampMergeUnionOnStrokeEnd: (v: boolean) => void;
    objectSvgs: UploadedObjectSvg[];
    parsedStampObjects: ParsedStampObject[];
    stampObjectInputRef: React.RefObject<HTMLInputElement>;
    setStampObjectFromPreset: (presetId: string) => void;
    removeStampObjectAt: (index: number) => void;
    handleStampObjectFileLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    hasContent: boolean;
    /** Same SVG as the visible canvas (paper + strokes; no debug overlay). */
    buildExportSvgString: () => string;
    downloadSvg: () => void;
    beginStroke: (samples: PointerSample[]) => void;
    appendStrokeSamples: (samples: PointerSample[]) => void;
    finishStroke: () => void;
    cancelActiveStroke: () => void;
    beginStampStroke: (samples: PointerSample[]) => void;
    appendStampStrokeSamples: (samples: PointerSample[]) => void;
    finishStampStroke: () => void;
    cancelActiveStampStroke: () => void;
    beginEraseStroke: (samples: PointerSample[]) => void;
    appendEraseStrokeSamples: (samples: PointerSample[]) => void;
    finishEraseStroke: () => void;
    clearCanvas: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    resetParameters: () => void;
    showPenDebug: boolean;
    setShowPenDebug: (v: boolean) => void;
    penSnapshot: PenPointerSnapshot;
    setPenSnapshot: (s: PenPointerSnapshot) => void;
    clearPenSnapshot: () => void;
    referenceImage: ArtbrushReferenceImage | null;
    referenceLoadError: string | null;
    showReferenceImage: boolean;
    setShowReferenceImage: (v: boolean) => void;
    referenceOpacity: number;
    setReferenceOpacity: (n: number) => void;
    includeReferenceInExport: boolean;
    setIncludeReferenceInExport: (v: boolean) => void;
    resizeCanvasToReferenceImage: boolean;
    setResizeCanvasToReferenceImage: (v: boolean) => void;
    referenceInputRef: React.RefObject<HTMLInputElement>;
    handleReferenceImageLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    clearReferenceImage: () => void;
    setReferenceDimensions: (width: number, height: number) => void;
}

const ArtbrushSvgCanvasContext = createContext<ArtbrushSvgCanvasState | null>(
    null,
);

export function useArtbrushSvgCanvas(): ArtbrushSvgCanvasState {
    const ctx = useContext(ArtbrushSvgCanvasContext);
    if (!ctx) {
        throw new Error(
            "useArtbrushSvgCanvas must be used within ArtbrushSvgCanvasProvider",
        );
    }
    return ctx;
}

interface ArtbrushSvgCanvasProviderProps {
    children: ReactNode;
}

export function ArtbrushSvgCanvasProvider({
    children,
}: ArtbrushSvgCanvasProviderProps) {
    const [showPenDebug, setShowPenDebug] = useState(false);
    const [penSnapshot, setPenSnapshot] =
        useState<PenPointerSnapshot>(IDLE_SNAPSHOT);
    const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE);
    const [minPressureFactor, setMinPressureFactor] = useState(
        DEFAULT_MIN_PRESSURE_FACTOR,
    );
    const [brushStrokeCount, setBrushStrokeCountState] = useState(
        DEFAULT_BRUSH_STROKE_COUNT,
    );
    const [brushMaxSpreadPx, setBrushMaxSpreadPxState] = useState(
        DEFAULT_BRUSH_MAX_SPREAD_PX,
    );
    const [strokeSmoothing, setStrokeSmoothingState] = useState(
        DEFAULT_STROKE_SMOOTHING,
    );
    const [eraserSize, setEraserSize] = useState(DEFAULT_ERASER_SIZE);
    const [color, setColorState] = useState(DEFAULT_COLOR);
    const [recentColors, setRecentColors] = useState<string[]>([
        DEFAULT_COLOR,
        "#e63946",
        "#457b9d",
        "#2a9d8f",
    ]);
    const [canvasWidth, setCanvasWidthState] = useState(DEFAULT_CANVAS_W);
    const [canvasHeight, setCanvasHeightState] = useState(DEFAULT_CANVAS_H);
    const [tool, setToolState] = useState<ArtbrushToolKind>("brush");
    const [strokes, setStrokes] = useState<BrushStroke[]>([]);
    const [activeStroke, setActiveStroke] = useState<BrushStroke[] | null>(null);
    const [activeBrushSamples, setActiveBrushSamples] = useState<
        BrushPointerSample[] | null
    >(null);
    const [stampStrokes, setStampStrokes] = useState<StampStroke[]>([]);
    const [activeStampStroke, setActiveStampStroke] =
        useState<ActiveStampStroke | null>(null);
    const [stampDistance, setStampDistance] = useState(DEFAULT_STAMP_DISTANCE);
    const [stampObjectSize, setStampObjectSize] = useState(
        DEFAULT_STAMP_OBJECT_SIZE,
    );
    const [stampPressureSizeEnabled, setStampPressureSizeEnabled] = useState(
        DEFAULT_STAMP_PRESSURE_SIZE_ENABLED,
    );
    const [stampPressureMinFactor, setStampPressureMinFactor] = useState(
        DEFAULT_STAMP_PRESSURE_MIN_FACTOR,
    );
    const [stampObjectPlacementMode, setStampObjectPlacementMode] = useState<
        "alternating" | "random"
    >(DEFAULT_STAMP_OBJECT_PLACEMENT_MODE);
    const [stampColorEnabled, setStampColorEnabled] = useState(
        DEFAULT_STAMP_COLOR_ENABLED,
    );
    const [stampColorMode, setStampColorMode] = useState<
        | "single"
        | "random-per-stroke"
        | "random-per-instance"
        | "random-palette-per-instance"
    >(DEFAULT_STAMP_COLOR_MODE);
    const [stampColorSingle, setStampColorSingle] = useState(
        DEFAULT_STAMP_COLOR_SINGLE,
    );
    const [stampColorPalette, setStampColorPalette] = useState<string[]>(
        DEFAULT_STAMP_COLOR_PALETTE,
    );
    const [stampPerpendicularScatterPx, setStampPerpendicularScatterPx] =
        useState(DEFAULT_STAMP_PERPENDICULAR_SCATTER_PX);
    const [stampBaseRotationDeg, setStampBaseRotationDeg] = useState(0);
    const [stampMergeUnionOnStrokeEnd, setStampMergeUnionOnStrokeEnd] =
        useState(DEFAULT_STAMP_MERGE_UNION_ON_STROKE_END);
    const [stampRotationMode, setStampRotationMode] =
        useState<StampRotationMode>(DEFAULT_STAMP_ROTATION_MODE);
    const [stampPolarRotationCenterXPct, setStampPolarRotationCenterXPct] =
        useState(DEFAULT_STAMP_POLAR_CENTER_PCT);
    const [stampPolarRotationCenterYPct, setStampPolarRotationCenterYPct] =
        useState(DEFAULT_STAMP_POLAR_CENTER_PCT);
    const [objectSvgs, setObjectSvgs] = useState<UploadedObjectSvg[]>(
        defaultStampObjectSvgs,
    );
    const stampObjectInputRef = useRef<HTMLInputElement>(null);
    const [referenceImage, setReferenceImage] =
        useState<ArtbrushReferenceImage | null>(null);
    const [referenceLoadError, setReferenceLoadError] = useState<string | null>(
        null,
    );
    const [showReferenceImage, setShowReferenceImage] = useState(true);
    const [referenceOpacity, setReferenceOpacity] = useState(
        DEFAULT_REFERENCE_OPACITY,
    );
    const [includeReferenceInExport, setIncludeReferenceInExport] =
        useState(false);
    const [resizeCanvasToReferenceImage, setResizeCanvasToReferenceImageState] =
        useState(DEFAULT_RESIZE_CANVAS_TO_REFERENCE);
    const referenceInputRef = useRef<HTMLInputElement>(null);
    const strokeIdRef = useRef(0);
    const activeBrushBaseStrokeIdRef = useRef<string | null>(null);
    // Refs mirror in-progress drawing state so commits don't rely on reading
    // values from inside React setters. Nesting setState calls inside other
    // setState updaters causes double-commits under React.StrictMode (the outer
    // updater is intentionally invoked twice, replaying the inner setState).
    const activeStampStrokeRef = useRef<ActiveStampStroke | null>(null);
    const activeStrokeRef = useRef<BrushStroke[] | null>(null);
    const activeBrushSamplesRef = useRef<BrushPointerSample[] | null>(null);
    const stampStrokesRef = useRef<StampStroke[]>([]);
    const history = useUndoRedo<ArtbrushDocument>({
        clone: cloneDocument,
    });

    useEffect(() => {
        stampStrokesRef.current = stampStrokes;
    }, [stampStrokes]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            const raw = window.localStorage.getItem(
                ARTBRUSH_CANVAS_DIMENSIONS_STORAGE_KEY,
            );
            if (!raw) return;
            const parsed = JSON.parse(raw) as
                | { width?: unknown; height?: unknown }
                | null;
            if (!parsed || typeof parsed !== "object") return;
            if (typeof parsed.width === "number") {
                setCanvasWidthState(clampCanvasDim(parsed.width));
            }
            if (typeof parsed.height === "number") {
                setCanvasHeightState(clampCanvasDim(parsed.height));
            }
        } catch {
            /* ignore malformed localStorage */
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            window.localStorage.setItem(
                ARTBRUSH_CANVAS_DIMENSIONS_STORAGE_KEY,
                JSON.stringify({
                    width: canvasWidth,
                    height: canvasHeight,
                }),
            );
        } catch {
            /* ignore private mode/quota errors */
        }
    }, [canvasWidth, canvasHeight]);

    const parsedStampObjects = useMemo(
        () =>
            objectSvgs
                .map((obj) => parseStampObjectSvg(obj.name, obj.content))
                .filter((obj): obj is ParsedStampObject => obj !== null),
        [objectSvgs],
    );

    const stampPolarCenter = useMemo(
        () => ({
            x: (canvasWidth * stampPolarRotationCenterXPct) / 100,
            y: (canvasHeight * stampPolarRotationCenterYPct) / 100,
        }),
        [
            canvasWidth,
            canvasHeight,
            stampPolarRotationCenterXPct,
            stampPolarRotationCenterYPct,
        ],
    );

    const stampRenderOptions = useMemo(
        (): StampRenderOptions => ({
            objectSize: stampObjectSize,
            anchor: DEFAULT_STAMP_ANCHOR,
            rotationMode: stampRotationMode,
            baseRotationDeg: stampBaseRotationDeg,
            pressureSizeEnabled: stampPressureSizeEnabled,
            pressureMinFactor: stampPressureMinFactor,
            objectPlacementMode: stampObjectPlacementMode,
            colorEnabled: stampColorEnabled,
            colorMode: stampColorMode,
            colorPalette: stampColorPalette,
            colorSingle: stampColorSingle,
            perpendicularScatterPx: stampPerpendicularScatterPx,
            polarCenter:
                stampRotationMode === "polar" ||
                stampRotationMode === "polar-inverted"
                    ? stampPolarCenter
                    : null,
        }),
        [
            stampObjectSize,
            stampRotationMode,
            stampBaseRotationDeg,
            stampPressureSizeEnabled,
            stampPressureMinFactor,
            stampObjectPlacementMode,
            stampPerpendicularScatterPx,
            stampColorEnabled,
            stampColorMode,
            stampColorPalette,
            stampColorSingle,
            stampPolarCenter,
        ],
    );

    const pushDocumentSnapshot = useCallback(() => {
        history.push({
            brushStrokes: cloneStrokes(strokes),
            stampStrokes: cloneStampStrokes(stampStrokes),
        });
    }, [history, strokes, stampStrokes]);

    const applyDocument = useCallback((doc: ArtbrushDocument) => {
        setStrokes(doc.brushStrokes);
        setStampStrokes(doc.stampStrokes);
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        activeStampStrokeRef.current = null;
        setActiveStroke(null);
        setActiveStampStroke(null);
    }, []);

    const setTool = useCallback((next: ArtbrushToolKind) => {
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        activeStampStrokeRef.current = null;
        setActiveStroke(null);
        setActiveStampStroke(null);
        setToolState(next);
    }, []);

    const setColor = useCallback((next: string) => {
        setColorState(next);
        setRecentColors((prev) => {
            const lower = next.toLowerCase();
            const filtered = prev.filter((c) => c.toLowerCase() !== lower);
            return [next, ...filtered].slice(0, MAX_RECENT_COLORS);
        });
    }, []);

    const nextStrokeId = useCallback(() => {
        strokeIdRef.current += 1;
        return `stroke-${strokeIdRef.current}`;
    }, []);

    const setCanvasWidth = useCallback((n: number) => {
        setCanvasWidthState(clampCanvasDim(n));
    }, []);

    const setCanvasHeight = useCallback((n: number) => {
        setCanvasHeightState(clampCanvasDim(n));
    }, []);

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (typeof values.canvasWidth === "number") {
            setCanvasWidthState(clampCanvasDim(values.canvasWidth));
        }
        if (typeof values.canvasHeight === "number") {
            setCanvasHeightState(clampCanvasDim(values.canvasHeight));
        }
        if (typeof values.showPenDebug === "boolean") {
            setShowPenDebug(values.showPenDebug);
        }
        if (typeof values.brushSize === "number") {
            setBrushSize(
                Math.max(2, Math.min(120, Math.round(values.brushSize))),
            );
        }
        if (typeof values.minPressureFactor === "number") {
            setMinPressureFactor(
                Math.max(0, Math.min(1, values.minPressureFactor)),
            );
        }
        if (typeof values.brushStrokeCount === "number") {
            setBrushStrokeCountState(
                Math.max(1, Math.min(11, Math.round(values.brushStrokeCount))),
            );
        }
        if (typeof values.brushMaxSpreadPx === "number") {
            setBrushMaxSpreadPxState(
                Math.max(0, Math.min(1000, Math.round(values.brushMaxSpreadPx))),
            );
        }
        if (typeof values.strokeSmoothing === "number") {
            setStrokeSmoothingState(
                Math.max(0, Math.min(100, Math.round(values.strokeSmoothing))),
            );
        }
        if (typeof values.color === "string") {
            setColorState(values.color);
        }
        if (Array.isArray(values.recentColors)) {
            const arr = (values.recentColors as unknown[]).filter(
                (v): v is string => typeof v === "string",
            );
            if (arr.length > 0) {
                setRecentColors(arr.slice(0, MAX_RECENT_COLORS));
            }
        }
        if (typeof values.showReferenceImage === "boolean") {
            setShowReferenceImage(values.showReferenceImage);
        }
        if (typeof values.referenceOpacity === "number") {
            setReferenceOpacity(
                Math.max(0.05, Math.min(1, values.referenceOpacity)),
            );
        }
        if (typeof values.includeReferenceInExport === "boolean") {
            setIncludeReferenceInExport(values.includeReferenceInExport);
        }
        if (typeof values.resizeCanvasToReferenceImage === "boolean") {
            setResizeCanvasToReferenceImageState(
                values.resizeCanvasToReferenceImage,
            );
        }
        if (
            values.tool === "brush" ||
            values.tool === "stamp" ||
            values.tool === "eraser"
        ) {
            setToolState(values.tool);
        }
        if (typeof values.eraserSize === "number") {
            setEraserSize(Math.max(4, Math.min(240, Math.round(values.eraserSize))));
        }
        if (typeof values.stampDistance === "number") {
            setStampDistance(
                Math.max(2, Math.min(500, Math.round(values.stampDistance))),
            );
        }
        if (typeof values.stampObjectSize === "number") {
            setStampObjectSize(
                Math.max(2, Math.min(500, Math.round(values.stampObjectSize))),
            );
        }
        if (typeof values.stampPressureSizeEnabled === "boolean") {
            setStampPressureSizeEnabled(values.stampPressureSizeEnabled);
        }
        if (typeof values.stampPressureMinFactor === "number") {
            setStampPressureMinFactor(
                Math.max(0, Math.min(1, values.stampPressureMinFactor)),
            );
        }
        if (
            values.stampObjectPlacementMode === "alternating" ||
            values.stampObjectPlacementMode === "random"
        ) {
            setStampObjectPlacementMode(values.stampObjectPlacementMode);
        }
        if (typeof values.stampPerpendicularScatterPx === "number") {
            setStampPerpendicularScatterPx(
                Math.max(0, Math.min(500, values.stampPerpendicularScatterPx)),
            );
        }
        if (typeof values.stampColorEnabled === "boolean") {
            setStampColorEnabled(values.stampColorEnabled);
        }
        if (
            values.stampColorMode === "single" ||
            values.stampColorMode === "random-per-stroke" ||
            values.stampColorMode === "random-per-instance" ||
            values.stampColorMode === "random-palette-per-instance"
        ) {
            setStampColorMode(values.stampColorMode);
        }
        if (typeof values.stampColorSingle === "string") {
            setStampColorSingle(values.stampColorSingle);
        }
        if (Array.isArray(values.stampColorPalette)) {
            const colors = (values.stampColorPalette as unknown[]).filter(
                (v): v is string => typeof v === "string",
            );
            if (colors.length > 0) setStampColorPalette(colors.slice(0, 12));
        }
        if (typeof values.stampBaseRotationDeg === "number") {
            setStampBaseRotationDeg(values.stampBaseRotationDeg);
        }
        if (typeof values.stampMergeUnionOnStrokeEnd === "boolean") {
            setStampMergeUnionOnStrokeEnd(values.stampMergeUnionOnStrokeEnd);
        }
        if (
            values.stampRotationMode === "align" ||
            values.stampRotationMode === "random" ||
            values.stampRotationMode === "random-90" ||
            values.stampRotationMode === "none" ||
            values.stampRotationMode === "polar" ||
            values.stampRotationMode === "polar-inverted"
        ) {
            setStampRotationMode(values.stampRotationMode);
        }
        if (typeof values.stampPolarRotationCenterXPct === "number") {
            setStampPolarRotationCenterXPct(
                Math.max(0, Math.min(100, values.stampPolarRotationCenterXPct)),
            );
        }
        if (typeof values.stampPolarRotationCenterYPct === "number") {
            setStampPolarRotationCenterYPct(
                Math.max(0, Math.min(100, values.stampPolarRotationCenterYPct)),
            );
        }
        if (typeof values.stampPresetId === "string") {
            const preset = OBJECT_SVG_PRESETS.find(
                (p) => p.id === values.stampPresetId,
            );
            if (preset && isValidSvg(preset.svg)) {
                setObjectSvgs([
                    { name: `${preset.label} (preset)`, content: preset.svg },
                ]);
            }
        }
    }, []);

    const getPresetValues = useCallback(
        () => ({
            canvasWidth,
            canvasHeight,
            tool,
            stampDistance,
            stampObjectSize,
            stampPressureSizeEnabled,
            stampPressureMinFactor,
            stampObjectPlacementMode,
            stampPerpendicularScatterPx,
            stampColorEnabled,
            stampColorMode,
            stampColorSingle,
            stampColorPalette,
            stampBaseRotationDeg,
            stampMergeUnionOnStrokeEnd,
            stampRotationMode,
            stampPolarRotationCenterXPct,
            stampPolarRotationCenterYPct,
            stampPresetId:
                OBJECT_SVG_PRESETS.find((p) => objectSvgs[0]?.content === p.svg)
                    ?.id ?? "square",
            showPenDebug,
            brushSize,
            minPressureFactor,
            brushStrokeCount,
            brushMaxSpreadPx,
            strokeSmoothing,
            eraserSize,
            color,
            recentColors,
            showReferenceImage,
            referenceOpacity,
            includeReferenceInExport,
            resizeCanvasToReferenceImage,
        }),
        [
            canvasWidth,
            canvasHeight,
            tool,
            stampDistance,
            stampObjectSize,
            stampPressureSizeEnabled,
            stampPressureMinFactor,
            stampObjectPlacementMode,
            stampPerpendicularScatterPx,
            stampColorEnabled,
            stampColorMode,
            stampColorSingle,
            stampColorPalette,
            stampBaseRotationDeg,
            stampMergeUnionOnStrokeEnd,
            stampRotationMode,
            stampPolarRotationCenterXPct,
            stampPolarRotationCenterYPct,
            showPenDebug,
            brushSize,
            minPressureFactor,
            brushStrokeCount,
            brushMaxSpreadPx,
            strokeSmoothing,
            eraserSize,
            color,
            recentColors,
            showReferenceImage,
            referenceOpacity,
            includeReferenceInExport,
            resizeCanvasToReferenceImage,
        ],
    );

    const { resetParameters: resetParamsRaw } = useToolParamPersistence({
        toolId: "artbrush-svg-canvas",
        defaultPreset: {
            canvasWidth: DEFAULT_CANVAS_W,
            canvasHeight: DEFAULT_CANVAS_H,
            showPenDebug: false,
            brushSize: DEFAULT_BRUSH_SIZE,
            minPressureFactor: DEFAULT_MIN_PRESSURE_FACTOR,
            brushStrokeCount: DEFAULT_BRUSH_STROKE_COUNT,
            brushMaxSpreadPx: DEFAULT_BRUSH_MAX_SPREAD_PX,
            strokeSmoothing: DEFAULT_STROKE_SMOOTHING,
            color: DEFAULT_COLOR,
            recentColors: [DEFAULT_COLOR, "#e63946", "#457b9d", "#2a9d8f"],
            showReferenceImage: true,
            referenceOpacity: DEFAULT_REFERENCE_OPACITY,
            includeReferenceInExport: false,
            resizeCanvasToReferenceImage: DEFAULT_RESIZE_CANVAS_TO_REFERENCE,
            tool: "brush",
            eraserSize: DEFAULT_ERASER_SIZE,
            stampDistance: DEFAULT_STAMP_DISTANCE,
            stampObjectSize: DEFAULT_STAMP_OBJECT_SIZE,
            stampPressureSizeEnabled: DEFAULT_STAMP_PRESSURE_SIZE_ENABLED,
            stampPressureMinFactor: DEFAULT_STAMP_PRESSURE_MIN_FACTOR,
            stampObjectPlacementMode: DEFAULT_STAMP_OBJECT_PLACEMENT_MODE,
            stampPerpendicularScatterPx: DEFAULT_STAMP_PERPENDICULAR_SCATTER_PX,
            stampColorEnabled: DEFAULT_STAMP_COLOR_ENABLED,
            stampColorMode: DEFAULT_STAMP_COLOR_MODE,
            stampColorSingle: DEFAULT_STAMP_COLOR_SINGLE,
            stampColorPalette: DEFAULT_STAMP_COLOR_PALETTE,
            stampBaseRotationDeg: 0,
            stampMergeUnionOnStrokeEnd: DEFAULT_STAMP_MERGE_UNION_ON_STROKE_END,
            stampRotationMode: DEFAULT_STAMP_ROTATION_MODE,
            stampPolarRotationCenterXPct: DEFAULT_STAMP_POLAR_CENTER_PCT,
            stampPolarRotationCenterYPct: DEFAULT_STAMP_POLAR_CENTER_PCT,
            stampPresetId: "square",
        },
        getPresetValues,
        applyPreset,
    });

    const clearPenSnapshot = useCallback(() => {
        setPenSnapshot(IDLE_SNAPSHOT);
    }, []);

    const applyCanvasSizeToReference = useCallback(
        (ref: ArtbrushReferenceImage) => {
            const { canvasW, canvasH } = canvasDimsForReferenceImage(
                ref.naturalWidth,
                ref.naturalHeight,
            );
            setCanvasWidthState(canvasW);
            setCanvasHeightState(canvasH);
            setReferenceImage({
                ...ref,
                ...referenceFullCanvasPlacement(canvasW, canvasH),
            });
        },
        [],
    );

    const setResizeCanvasToReferenceImage = useCallback(
        (enabled: boolean) => {
            setResizeCanvasToReferenceImageState(enabled);
            if (enabled && referenceImage) {
                applyCanvasSizeToReference(referenceImage);
            }
        },
        [referenceImage, applyCanvasSizeToReference],
    );

    const handleReferenceImageLoad = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            if (!file.type.startsWith("image/")) {
                setReferenceLoadError("Please choose a raster image file.");
                return;
            }
            setReferenceLoadError(null);
            try {
                let dataUrl = await readFileAsDataUrl(file);
                const size = await loadImageNaturalSize(dataUrl);
                const scaled = await downscaleRasterDataUrlIfNeeded(
                    dataUrl,
                    size.w,
                    size.h,
                    LATTICE_RASTER_MAX_EDGE,
                );
                dataUrl = scaled.dataUrl;
                let canvasW = canvasWidth;
                let canvasH = canvasHeight;
                let placement: Pick<
                    ArtbrushReferenceImage,
                    "x" | "y" | "width" | "height"
                >;
                if (resizeCanvasToReferenceImage) {
                    ({ canvasW, canvasH } = canvasDimsForReferenceImage(
                        scaled.w,
                        scaled.h,
                    ));
                    setCanvasWidthState(canvasW);
                    setCanvasHeightState(canvasH);
                    placement = referenceFullCanvasPlacement(canvasW, canvasH);
                } else {
                    placement = fitReferenceImageToCanvas(
                        scaled.w,
                        scaled.h,
                        canvasW,
                        canvasH,
                    );
                }
                setReferenceImage({
                    dataUrl,
                    name: file.name,
                    naturalWidth: scaled.w,
                    naturalHeight: scaled.h,
                    ...placement,
                });
                setShowReferenceImage(true);
            } catch (err) {
                setReferenceLoadError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load image.",
                );
            }
        },
        [canvasWidth, canvasHeight, resizeCanvasToReferenceImage],
    );

    const clearReferenceImage = useCallback(() => {
        setReferenceImage(null);
        setReferenceLoadError(null);
    }, []);

    const setReferenceDimensions = useCallback((width: number, height: number) => {
        setReferenceImage((prev) => {
            if (!prev) return prev;
            const placement = resizeReferenceKeepingCenter(prev, width, height);
            return { ...prev, ...placement };
        });
    }, []);

    const setStampObjectFromPreset = useCallback((presetId: string) => {
        const preset = OBJECT_SVG_PRESETS.find((p) => p.id === presetId);
        if (!preset || !isValidSvg(preset.svg)) return;
        setObjectSvgs((prev) => [
            ...prev,
            { name: `${preset.label} (preset)`, content: preset.svg },
        ]);
    }, []);

    const removeStampObjectAt = useCallback((index: number) => {
        setObjectSvgs((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleStampObjectFileLoad = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                const text = reader.result;
                if (typeof text !== "string" || !isValidSvg(text)) return;
                setObjectSvgs((prev) => [
                    ...prev,
                    { name: file.name, content: text },
                ]);
            };
            reader.readAsText(file);
        },
        [],
    );

    const beginStroke = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0) return;
            pushDocumentSnapshot();
            const sourceSamples: BrushPointerSample[] = [...samples];
            activeBrushSamplesRef.current = sourceSamples;
            setActiveBrushSamples(sourceSamples);
            const baseStrokeId = nextStrokeId();
            activeBrushBaseStrokeIdRef.current = baseStrokeId;
            const built = buildMultiBrushStrokesFromSamples(sourceSamples, {
                baseStrokeId,
                color,
                brushSize,
                minPressureFactor,
                strokeCount: brushStrokeCount,
                maxSpreadPx: brushMaxSpreadPx,
            });
            activeStrokeRef.current = built;
            setActiveStroke(built);
        },
        [
            pushDocumentSnapshot,
            brushSize,
            minPressureFactor,
            brushStrokeCount,
            brushMaxSpreadPx,
            color,
            nextStrokeId,
        ],
    );

    const beginStampStroke = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0 || parsedStampObjects.length === 0) return;
            pushDocumentSnapshot();
            const trail = samplesToTrailPoints(samples);
            const first = trail[0];
            if (!first) return;
            let stroke = createActiveStampStroke(
                nextStrokeId(),
                first,
                stampDistance,
                nextStrokeId,
                parsedStampObjects.length,
                stampRenderOptions,
            );
            if (trail.length > 1) {
                stroke = extendStampStrokeWithPoints(
                    stroke,
                    trail.slice(1),
                    stampDistance,
                    nextStrokeId,
                    parsedStampObjects.length,
                    stampRenderOptions,
                );
            }
            activeStampStrokeRef.current = stroke;
            setActiveStampStroke(stroke);
        },
        [
            pushDocumentSnapshot,
            parsedStampObjects.length,
            stampDistance,
            nextStrokeId,
            stampRenderOptions,
        ],
    );

    const appendStampStrokeSamples = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0) return;
            const prev = activeStampStrokeRef.current;
            if (!prev) return;
            const trail = samplesToTrailPoints(samples);
            let startIdx = 0;
            if (prev.trail.length > 0) {
                const last = prev.trail[prev.trail.length - 1]!;
                // Float coordinates from smoothed events rarely match exactly.
                // Keep a small tolerance when searching overlap.
                const EPS = 0.75;
                let bestIdx = -1;
                let bestDist = Number.POSITIVE_INFINITY;
                for (let i = 0; i < trail.length; i += 1) {
                    const p = trail[i]!;
                    const d = Math.hypot(p.x - last.x, p.y - last.y);
                    if (d < bestDist) {
                        bestDist = d;
                        bestIdx = i;
                    }
                }
                startIdx = bestDist <= EPS && bestIdx >= 0 ? bestIdx + 1 : 0;
            }
            const segment = trail.slice(startIdx);
            if (segment.length === 0) return;
            const next = extendStampStrokeWithPoints(
                prev,
                segment,
                stampDistance,
                nextStrokeId,
                parsedStampObjects.length,
                stampRenderOptions,
            );
            activeStampStrokeRef.current = next;
            setActiveStampStroke(next);
        },
        [parsedStampObjects.length, stampDistance, nextStrokeId, stampRenderOptions],
    );

    const finishStampStroke = useCallback(() => {
        const stroke = activeStampStrokeRef.current;
        activeStampStrokeRef.current = null;
        if (!stroke || stroke.stamps.length === 0) {
            setActiveStampStroke(null);
            return;
        }
        let finalized = finalizeStampStroke(stroke);
        const pathIndex = stampStrokesRef.current.length;
        if (
            stampMergeUnionOnStrokeEnd &&
            parsedStampObjects.length === 1 &&
            finalized.stamps.length > 0
        ) {
            finalized = applyStampStrokeUnion(
                finalized,
                parsedStampObjects[0]!,
                stampRenderOptions,
                pathIndex,
            );
        }
        setActiveStampStroke(null);
        setStampStrokes((s) => [...s, finalized]);
    }, [
        stampMergeUnionOnStrokeEnd,
        parsedStampObjects,
        stampRenderOptions,
    ]);

    const cancelActiveStampStroke = useCallback(() => {
        activeStampStrokeRef.current = null;
        setActiveStampStroke(null);
    }, []);

    const eraseWithSamples = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0) return;
            const radius = Math.max(2, eraserSize / 2);
            setStrokes((prev) =>
                prev.filter(
                    (stroke) =>
                        !samples.some((sample) =>
                            strokeIntersectsEraser(stroke, sample, radius),
                        ),
                ),
            );
            setStampStrokes((prev) =>
                prev
                    .map((stroke) => {
                        const kept = stroke.stamps.filter(
                            (stamp) =>
                                !samples.some(
                                    (sample) =>
                                        Math.hypot(
                                            stamp.x - sample.x,
                                            stamp.y - sample.y,
                                        ) <= radius,
                                ),
                        );
                        if (kept.length === stroke.stamps.length) return stroke;
                        if (kept.length === 0) return null;
                        return { ...stroke, stamps: kept, unionPathD: undefined, unionFill: undefined };
                    })
                    .filter((v): v is StampStroke => v !== null),
            );
        },
        [eraserSize],
    );

    const beginEraseStroke = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0) return;
            pushDocumentSnapshot();
            eraseWithSamples(samples);
        },
        [eraseWithSamples, pushDocumentSnapshot],
    );

    const appendEraseStrokeSamples = useCallback(
        (samples: PointerSample[]) => {
            eraseWithSamples(samples);
        },
        [eraseWithSamples],
    );

    const finishEraseStroke = useCallback(() => {}, []);

    const appendStrokeSamples = useCallback(
        (samples: PointerSample[]) => {
            if (samples.length === 0) return;
            const prev = activeBrushSamplesRef.current;
            if (!prev) return;
            const merged: BrushPointerSample[] = [...prev, ...samples];
            const baseStrokeId =
                activeBrushBaseStrokeIdRef.current ?? nextStrokeId();
            const built = buildMultiBrushStrokesFromSamples(merged, {
                baseStrokeId,
                color,
                brushSize,
                minPressureFactor,
                strokeCount: brushStrokeCount,
                maxSpreadPx: brushMaxSpreadPx,
            });
            activeBrushSamplesRef.current = merged;
            activeStrokeRef.current = built;
            setActiveBrushSamples(merged);
            setActiveStroke(built);
        },
        [
            brushSize,
            minPressureFactor,
            brushStrokeCount,
            brushMaxSpreadPx,
            color,
            nextStrokeId,
        ],
    );

    const finishStroke = useCallback(() => {
        const prev = activeStrokeRef.current;
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        setActiveBrushSamples(null);
        if (!prev || prev.length === 0) {
            setActiveStroke(null);
            return;
        }
        const nonEmpty = prev.filter((stroke) => stroke.points.length > 0);
        setActiveStroke(null);
        if (nonEmpty.length === 0) return;
        setStrokes((s) => [...s, ...nonEmpty]);
    }, []);

    const cancelActiveStroke = useCallback(() => {
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        setActiveStroke(null);
        setActiveBrushSamples(null);
    }, []);

    const clearCanvas = useCallback(() => {
        if (
            strokes.length === 0 &&
            !activeStroke &&
            stampStrokes.length === 0 &&
            !activeStampStroke
        ) {
            return;
        }
        pushDocumentSnapshot();
        setStrokes([]);
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        activeStampStrokeRef.current = null;
        setActiveStroke(null);
        setActiveBrushSamples(null);
        setStampStrokes([]);
        setActiveStampStroke(null);
    }, [
        strokes,
        activeStroke,
        stampStrokes,
        activeStampStroke,
        pushDocumentSnapshot,
    ]);

    const undo = useCallback(() => {
        history.undo(
            { brushStrokes: strokes, stampStrokes },
            applyDocument,
        );
    }, [history, strokes, stampStrokes, applyDocument]);

    const redo = useCallback(() => {
        history.redo(
            { brushStrokes: strokes, stampStrokes },
            applyDocument,
        );
    }, [history, strokes, stampStrokes, applyDocument]);

    const resetParameters = useCallback(() => {
        history.clear();
        resetParamsRaw();
        setStrokes([]);
        activeStrokeRef.current = null;
        activeBrushSamplesRef.current = null;
        activeBrushBaseStrokeIdRef.current = null;
        activeStampStrokeRef.current = null;
        setActiveStroke(null);
        setActiveBrushSamples(null);
        setStampStrokes([]);
        setActiveStampStroke(null);
        setObjectSvgs(defaultStampObjectSvgs());
        setReferenceImage(null);
        setReferenceLoadError(null);
    }, [history, resetParamsRaw]);

    const viewBoxAttr = `0 0 ${canvasWidth} ${canvasHeight}`;
    const hasContent =
        strokes.length > 0 ||
        activeStroke !== null ||
        stampStrokes.length > 0 ||
        activeStampStroke !== null;

    const buildExportSvgString = useCallback(
        () =>
            buildArtbrushExportSvgString(
                strokes,
                activeStroke,
                canvasWidth,
                canvasHeight,
                {
                    referenceImage,
                    referenceOpacity,
                    renderReference:
                        !!referenceImage && includeReferenceInExport,
                    stampStrokes,
                    activeStampStroke,
                    stampObjects: parsedStampObjects,
                    stampRenderOptions,
                },
            ),
        [
            strokes,
            activeStroke,
            canvasWidth,
            canvasHeight,
            referenceImage,
            referenceOpacity,
            includeReferenceInExport,
            stampStrokes,
            activeStampStroke,
            parsedStampObjects,
            stampRenderOptions,
        ],
    );

    const downloadSvg = useCallback(() => {
        const doc = buildExportSvgString();
        const blob = new Blob([doc], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `artbrush-svg-${canvasWidth}x${canvasHeight}-${Date.now()}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, canvasWidth, canvasHeight]);

    const value = useMemo(
        (): ArtbrushSvgCanvasState => ({
            tool,
            setTool,
            canvasWidth,
            canvasHeight,
            setCanvasWidth,
            setCanvasHeight,
            viewBoxAttr,
            brushSize,
            setBrushSize,
            minPressureFactor,
            setMinPressureFactor,
            brushStrokeCount,
            setBrushStrokeCount: (n: number) =>
                setBrushStrokeCountState(
                    Math.max(1, Math.min(11, Math.round(n))),
                ),
            brushMaxSpreadPx,
            setBrushMaxSpreadPx: (n: number) =>
                setBrushMaxSpreadPxState(
                    Math.max(0, Math.min(1000, Math.round(n))),
                ),
            strokeSmoothing,
            setStrokeSmoothing: (n: number) =>
                setStrokeSmoothingState(
                    Math.max(0, Math.min(100, Math.round(n))),
                ),
            eraserSize,
            setEraserSize: (n: number) =>
                setEraserSize(Math.max(4, Math.min(240, Math.round(n)))),
            color,
            setColor,
            recentColors,
            strokes,
            activeStroke,
            stampStrokes,
            activeStampStroke,
            stampDistance,
            setStampDistance,
            stampObjectSize,
            setStampObjectSize,
            stampPressureSizeEnabled,
            setStampPressureSizeEnabled,
            stampPressureMinFactor,
            setStampPressureMinFactor,
            stampObjectPlacementMode,
            setStampObjectPlacementMode,
            stampPerpendicularScatterPx,
            setStampPerpendicularScatterPx,
            stampColorEnabled,
            setStampColorEnabled,
            stampColorMode,
            setStampColorMode,
            stampColorSingle,
            setStampColorSingle,
            stampColorPalette,
            setStampColorPalette,
            stampBaseRotationDeg,
            setStampBaseRotationDeg,
            stampRotationMode,
            setStampRotationMode,
            stampPolarRotationCenterXPct,
            setStampPolarRotationCenterXPct,
            stampPolarRotationCenterYPct,
            setStampPolarRotationCenterYPct,
            stampRenderOptions,
            stampAnchor: DEFAULT_STAMP_ANCHOR,
            stampMergeUnionOnStrokeEnd,
            setStampMergeUnionOnStrokeEnd,
            objectSvgs,
            parsedStampObjects,
            stampObjectInputRef,
            setStampObjectFromPreset,
            removeStampObjectAt,
            handleStampObjectFileLoad,
            hasContent,
            buildExportSvgString,
            downloadSvg,
            beginStroke,
            appendStrokeSamples,
            finishStroke,
            cancelActiveStroke,
            beginStampStroke,
            appendStampStrokeSamples,
            finishStampStroke,
            cancelActiveStampStroke,
            beginEraseStroke,
            appendEraseStrokeSamples,
            finishEraseStroke,
            clearCanvas,
            undo,
            redo,
            canUndo: history.canUndo,
            canRedo: history.canRedo,
            resetParameters,
            showPenDebug,
            setShowPenDebug,
            penSnapshot,
            setPenSnapshot,
            clearPenSnapshot,
            referenceImage,
            referenceLoadError,
            showReferenceImage,
            setShowReferenceImage,
            referenceOpacity,
            setReferenceOpacity,
            includeReferenceInExport,
            setIncludeReferenceInExport,
            resizeCanvasToReferenceImage,
            setResizeCanvasToReferenceImage,
            referenceInputRef,
            handleReferenceImageLoad,
            clearReferenceImage,
            setReferenceDimensions,
        }),
        [
            tool,
            setTool,
            canvasWidth,
            canvasHeight,
            setCanvasWidth,
            setCanvasHeight,
            viewBoxAttr,
            brushSize,
            minPressureFactor,
            brushStrokeCount,
            brushMaxSpreadPx,
            strokeSmoothing,
            eraserSize,
            color,
            recentColors,
            strokes,
            activeStroke,
            stampStrokes,
            activeStampStroke,
            stampDistance,
            stampObjectSize,
            stampPressureSizeEnabled,
            stampPressureMinFactor,
            stampObjectPlacementMode,
            stampPerpendicularScatterPx,
            stampColorEnabled,
            stampColorMode,
            stampColorSingle,
            stampColorPalette,
            stampBaseRotationDeg,
            stampRotationMode,
            stampPolarRotationCenterXPct,
            stampPolarRotationCenterYPct,
            stampRenderOptions,
            stampMergeUnionOnStrokeEnd,
            parsedStampObjects,
            objectSvgs,
            hasContent,
            buildExportSvgString,
            downloadSvg,
            beginStroke,
            appendStrokeSamples,
            finishStroke,
            cancelActiveStroke,
            beginStampStroke,
            appendStampStrokeSamples,
            finishStampStroke,
            cancelActiveStampStroke,
            beginEraseStroke,
            appendEraseStrokeSamples,
            finishEraseStroke,
            clearCanvas,
            undo,
            redo,
            history.canUndo,
            history.canRedo,
            resetParameters,
            showPenDebug,
            penSnapshot,
            clearPenSnapshot,
            referenceImage,
            referenceLoadError,
            showReferenceImage,
            referenceOpacity,
            includeReferenceInExport,
            resizeCanvasToReferenceImage,
            handleReferenceImageLoad,
            clearReferenceImage,
            setReferenceDimensions,
            setStampObjectFromPreset,
            removeStampObjectAt,
            handleStampObjectFileLoad,
        ],
    );

    return (
        <ArtbrushSvgCanvasContext.Provider value={value}>
            {children}
        </ArtbrushSvgCanvasContext.Provider>
    );
}
